import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";

const WebSocketContext = createContext();

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsUrl = process.env.REACT_APP_WS_URL;
  const seen = useRef(new Set()); // track already received alerts
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // Start with 1 second

  const connect = useCallback(() => {
    // Clear any existing reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    console.log("🔗 Connecting to WebSocket:", wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("✅ WebSocket connected successfully");
      setIsConnected(true);
      reconnectAttemptsRef.current = 0; // Reset reconnection attempts on successful connection
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle "validations_solved" event format
        if (data.event === "validations_solved" && Array.isArray(data.solved)) {
          console.log("📩 Received validations_solved event with", data.solved.length, "alerts");
          
          data.solved.forEach((alert) => {
            // Deduplication key for solved alerts
            const key = `${alert.alert_id}-${alert.validation_check}-${alert.message}`;
            
            if (seen.current.has(key)) {
              console.log("⚠️ Duplicate notification received, skipping:", key);
              return;
            }
            
            seen.current.add(key);
            console.log("📩 New solved alert received:", alert);

            // Transform to match your notification format
            const notification = {
              id: alert.alert_id,
              type: alert.validation_check || "Alert",
              message: alert.message,
              reason: alert.reason,
              timestamp: new Date().toISOString(),
              read: false,
              transaction_set_id: data.transaction_set_id,
              event: "validations_solved"
            };

            setNotifications((prev) => [notification, ...prev]);
          });
        } 
        // Handle original notification format
        else {
          // Deduplication key (based on validation_id + type + message)
          const key = `${data.validation_id}-${data.type}-${data.message}`;

          if (seen.current.has(key)) {
            console.log("⚠️ Duplicate notification received, skipping:", key);
            return; // skip duplicates
          }
          
          seen.current.add(key);
          console.log("📩 New notification received:", data);

          setNotifications((prev) => [data, ...prev]); // prepend new notification
        }
      } catch (error) {
        console.error("❌ Failed to parse WebSocket message:", error);
      }
    };

    ws.onclose = (event) => {
      console.warn("⚠️ WebSocket disconnected. Code:", event.code, "Reason:", event.reason);
      setIsConnected(false);
      wsRef.current = null;

      // Attempt to reconnect with exponential backoff
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay = Math.min(
          baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current),
          30000 // Max 30 seconds between reconnection attempts
        );
        
        reconnectAttemptsRef.current += 1;
        console.log(
          `🔄 Attempting to reconnect... (Attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}) in ${delay}ms`
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        console.error("❌ Max reconnection attempts reached. Please refresh the page.");
      }
    };

    ws.onerror = (err) => {
      console.error("❌ WebSocket error:", err);
      // The onclose event will handle reconnection
    };

    return ws;
  }, [wsUrl]);

  useEffect(() => {
    connect();

    // Cleanup function
    return () => {
      console.log("🔌 Cleaning up WebSocket connection");
      
      // Clear reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      // Close WebSocket connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Optional: Add a manual reconnect function
  const reconnect = useCallback(() => {
    console.log("🔄 Manual reconnection triggered");
    reconnectAttemptsRef.current = 0; // Reset attempts for manual reconnect
    connect();
  }, [connect]);

  const markAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const deleteNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
    seen.current.clear();
  };

  return (
    <WebSocketContext.Provider 
      value={{ 
        notifications, 
        markAsRead, 
        deleteNotification,
        clearNotifications, 
        isConnected,
        reconnect 
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};